import axios from 'axios';
import type { NextPage } from 'next';
import { useRouter } from 'next/router';
import { useQuiz } from '../_shared/useQuiz';
import { useLocalization } from '~/framework';
import { calculatePassingGrade } from '../_shared';
import { ActiveNavItem, ErrorPage } from '~/page-components/Layout';
import { Stack, Text, Button } from '@chakra-ui/react';
import { PageTitle, useSiteInfo } from '~/pages/_shared';
import { Center, CircularProgress } from '@chakra-ui/react';
import { useCourseAssignmentOperatorDetails } from '~/pages/Courses/[courseAssignmentId]/useCourseAssignmentOperatorDetails';

const StartQuiz: NextPage = () => {
   const router = useRouter();
   const { _js } = useLocalization();
   const { data: siteInfo } = useSiteInfo();
   const quizid = Number(router?.query.quizid);
   let quizAttemptsRemaining: undefined | number;
   const quizRevisionid = Number(router?.query.quiz_revisionid);
   const { data: quiz } = useQuiz(quizid, false, false, quizRevisionid);

   const guideid = Number(router?.query.guideid || router?.query.guide);
   const wikiid = Number(router?.query.wikiid || router?.query.wiki);
   const courseAssignmentid = router?.query.assignmentId
      ? Number(router?.query.assignmentId)
      : null;
   const response = useCourseAssignmentOperatorDetails(
      courseAssignmentid,
      siteInfo?.['dev-limit-quiz-attempts']
   );

   if (axios.isAxiosError(response.error)) {
      return <ErrorPage error={{ code: response.error.status ?? 403 }} />;
   }

   if (siteInfo?.['dev-limit-quiz-attempts'] && response) {
      const courseAssignmentDetails = response.data;
      const courseAssignment = courseAssignmentDetails?.courseAssignment;
      const courseAssignmentStages = courseAssignment?.user_completion_status;
      if (courseAssignmentStages) {
         for (const item of courseAssignmentStages) {
            if (item.stage.docid == guideid && item.stage.doctype == 'guide') {
               quizAttemptsRemaining = item.stage.quiz_attempts_remaining;
            }
         }
      }
   }

   if (!siteInfo?.['dev-feature-quizzes']) {
      return <ErrorPage error={{ code: 404 }} />;
   }

   if (!quiz) {
      return (
         <Center height="100vh" width="100vw">
            <CircularProgress isIndeterminate />
         </Center>
      );
   }

   const questionsLength = quiz.nonMediaQuestionsCount || 0;

   const startQuizSession = () => {
      if (courseAssignmentid) {
         router.push(
            `/Quizzes/${quizid}?assignmentId=${courseAssignmentid}&quiz_revisionid=${quizRevisionid}`
         );
      } else if (guideid) {
         router.push(`/Quizzes/${quizid}?guideid=${guideid}&quiz_revisionid=${quizRevisionid}`);
      } else if (wikiid) {
         router.push(`/Quizzes/${quizid}?wikiid=${wikiid}&quiz_revisionid=${quizRevisionid}`);
      } else {
         router.push(`/Quizzes/${quizid}?quiz_revisionid=${quizRevisionid}`);
      }
   };

   return (
      <>
         <PageTitle>{quiz.quiz_json.title}</PageTitle>
         <ActiveNavItem value="quizzes" />
         <Stack spacing="2" py="6" align="center">
            <Text fontWeight="semibold" fontSize="4xl" lineHeight="normal" pt="32" pb="10">
               {quiz.quiz_json.title}
            </Text>
            <Stack direction="row" spacing="10">
               <Stack>
                  <Text fontWeight="normal" fontSize="lg" lineHeight="normal">
                     {_js('Number of questions')}
                  </Text>
                  {quizAttemptsRemaining && (
                     <Text fontWeight="normal" fontSize="lg" lineHeight="normal">
                        {_js('Attempts remaining')}
                     </Text>
                  )}
                  <Text fontWeight="normal" fontSize="lg" lineHeight="normal">
                     {_js('Passing score')}
                  </Text>
               </Stack>
               <Stack>
                  <Text fontWeight="normal" fontSize="lg" lineHeight="normal">
                     {questionsLength}
                  </Text>
                  {quizAttemptsRemaining && (
                     <Text fontWeight="normal" fontSize="lg" lineHeight="normal">
                        {quizAttemptsRemaining}
                     </Text>
                  )}
                  <Text fontWeight="normal" fontSize="lg" lineHeight="normal" pb="50">
                     {`${quiz.quiz_revision.passing_score}% (${calculatePassingGrade(
                        quiz.quiz_revision.passing_score,
                        questionsLength
                     )}/${questionsLength})`}
                  </Text>
               </Stack>
            </Stack>
            <Stack direction="row" spacing="7" pb="32">
               <Button
                  borderRadius="md"
                  onClick={() => router.back()}
                  px="10"
                  shadow="md"
                  variant="solid"
                  colorScheme="white"
                  color="red"
               >
                  {_js('Cancel')}
               </Button>
               <Button
                  borderRadius="md"
                  onClick={startQuizSession}
                  px="10"
                  shadow="md"
                  variant="solid"
                  colorScheme="blue"
               >
                  {_js('Start Quiz')}
               </Button>
            </Stack>
         </Stack>
      </>
   );
};

export default StartQuiz;
